<template>
  <div class="card" :class="{ 'card-primary': isBorderHighlighted }">
    <div class="plan-top-info">
      <div class="plan-tit">
        <div class="flex-btw">
          <div class="inr">
            <span class="tit-h4">{{ typePlanCamel }}</span>

            <span v-if="plan === 'PRO'" class="pc-badge blue-grdient">{{ t('popular_products') }}</span>
            <span v-if="isAnnualDiscountVisible" class="pc-badge primary">
              {{ t('on_sale_percent', { discount: 20 }) }}
            </span>
          </div>

          <div v-if="subscriptionStore.currentPlan === props.plan" class="my-ck">
            <IconCheck />
          </div>
        </div>

        <p class="sub-txt">{{ planDescription }}</p>
      </div>

      <div class="price-wrap">
        <div class="plan-price">
          <h3 class="tit-h3">{{ planPrice }}</h3>
          <p v-if="!['FREE', 'ENTERPRISE'].includes(plan)" class="dim">/ {{ t('label_month') }}</p>
        </div>
      </div>
    </div>

    <button
      class="xl-btn"
      :class="{ 'gray-btn': !isChangePlan, 'btn-primary': isChangePlan }"
      :disabled="isDisabled"
      @click="handlePlanClick"
    >
      {{ planButtonLabel }}
    </button>

    <div class="benefit-area">
      <ul class="benefit-list">
        <li>
          <div class="icon-tit">
            <img :src="getAssetImageUrl('common/icons/credit.svg')" alt="credit" />
            <p class="category-tit">{{ t('ss_credits') }}</p>
          </div>
          <ul class="bf-item">
            <li>
              <IconCheck />
              <span>{{ isEnterprise ? t('inquiry') : creditPerMonth }}</span>
            </li>
            <li>
              <IconCheck />
              <span>{{ getCreditDiscountLabel(plan) }}</span>
            </li>
          </ul>
        </li>

        <li v-if="languageSupport">
          <div class="icon-tit">
            <img :src="getAssetImageUrl('common/icons/earth-icon.svg')" alt="" />
            <p class="category-tit">{{ t('language_support') }}</p>
          </div>
          <ul class="bf-item">
            <li>
              <IconCheck />
              <span>{{ languageSupport }}</span>
            </li>
          </ul>
        </li>

        <li v-if="interpretationList?.length > 0">
          <div class="icon-tit">
            <img :src="getAssetImageUrl('common/icons/interpreting-icon.svg')" alt="" />
            <p class="category-tit">{{ t('label_interpretation') }}</p>
          </div>
          <ul class="bf-item">
            <li v-for="item in interpretationList" :key="item">
              <IconCheck />
              <span>{{ item }}</span>
            </li>
          </ul>
        </li>

        <li v-if="additionalList?.length > 0">
          <div class="icon-tit">
            <img :src="additionalListImageUrl" alt="" />
            <p class="category-tit">{{ isEnterprise ? t('custom_support') : t('additional_features') }}</p>
          </div>

          <ul class="bf-item">
            <li v-for="item in additionalList" :key="item">
              <IconCheck />
              <span>{{ item }}</span>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { camelCase, capitalize } from 'lodash';

import IconCheck from '@/components/icons/IconCheck.vue';
import { useModalSurvey } from '@/composables/domains/self-service/useModalSurvey';
import { useGtag } from '@/composables/shared/useGtag';
import { usePayment } from '@/composables/shared/usePayment';
import { usePlan } from '@/composables/shared/usePlan';
import { useSubscriptionStore } from '@/stores/subscription';
import { Nullable } from '@/types/common';
import { PlanPricing, TypePlan } from '@/types/components';
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';

const props = withDefaults(
  defineProps<{
    plan: TypePlan;
    planDetails: { language: string; price: string; pricePerChat: string[]; features: string[] };
    planPrices?: Record<string, PlanPricing>;
    isInKorea?: boolean;
    billingCycle?: 'MONTHLY' | 'YEARLY';
  }>(),
  {
    isInKorea: true,
    planDetails: () => ({ language: '', price: '', pricePerChat: [], features: [] }),
    planPrices: () => ({}),
  }
);

const { t } = useI18n();
const router = useRouter();

const subscriptionStore = useSubscriptionStore();

const { formatCurrency, getPlanButtonText, getPlanDisabledState, getCreditDiscountLabel } = usePlan();
const { setTypeSurveyUnsubscribe, openModalSurvey } = useModalSurvey();
const {
  hasRegisteredCard,
  openCardRegisterPopup,
  openCardManagePopup,
  openSubscriptionPaymentPopup,
  openResubscriptionPopup,
} = usePayment();
const { trackEventFunnel } = useGtag();

const creditPerMonth = ref<Nullable<string>>(null);
const languageSupport = ref<Nullable<string>>(null);
const interpretationList = ref<string[]>([]);
const additionalList = ref<string[]>([]);

const isEnterprise = computed(() => props.plan === 'ENTERPRISE');
const isChangePlan = computed(() => {
  if (['FREE', 'ENTERPRISE'].includes(props.plan)) {
    return false;
  }

  return props.plan !== subscriptionStore.currentPlan && subscriptionStore.currentPlanStatus !== 'PLANNED';
});

const planPrice = computed(() => {
  if (props.plan === 'FREE') return t('ss_price_free');
  if (props.plan === 'ENTERPRISE') return '';

  const pricing = props.planPrices?.[props.plan]?.[subscriptionStore.currency];
  if (!pricing) return '';

  const rawPrice = props.billingCycle === 'MONTHLY' ? pricing.pricePerMonth : Math.round(pricing.pricePerYear / 12);

  return formatCurrency(rawPrice, subscriptionStore.currency);
});

const planDescription = computed(() => {
  if (props.plan === 'FREE') return t('subscription_experience');
  else if (props.plan === 'STARTER') return t('subscription_light_experience');
  else if (props.plan === 'PRO') return t('subscription_continuous_interpretation');
  return `${t('perfect_service_support')}  ${t('contact_us_freely')}`;
});

const isBorderHighlighted = computed(() => {
  const userPlan = subscriptionStore.currentPlan;

  if (
    (userPlan === 'FREE' && props.plan === 'STARTER') ||
    (userPlan === 'STARTER' && props.plan === 'PRO') ||
    (userPlan === 'PRO' && props.plan === 'PRO')
  ) {
    return true;
  }

  return false;
});

const isAnnualDiscountVisible = computed(() => {
  if (subscriptionStore.currentPlanDetails?.billingCycle === 'MONTHLY') return false;
  return ['STARTER', 'PRO'].includes(props.plan);
});

const isDisabled = computed(() => {
  return getPlanDisabledState(props.plan);
});

const planButtonLabel = computed(() => {
  return getPlanButtonText(props.plan);
});

const additionalListImageUrl = computed(() =>
  isEnterprise.value
    ? getAssetImageUrl('common/icons/custom-icon.svg')
    : getAssetImageUrl('common/icons/plus-function-icon.svg')
);

const typePlanCamel = capitalize(camelCase(props.plan));

const handlePlanClick = async () => {
  trackEventFunnel('click_plan_button', { plan: props.plan, billingCycle: props.billingCycle });

  if (props.plan === 'ENTERPRISE') {
    goToContactPage();
    return;
  }

  if (props.plan === 'FREE' && ['STARTER', 'PRO'].includes(subscriptionStore.currentPlan)) {
    openUnSubscriptionPopup();
    return;
  }

  if (props.plan === subscriptionStore.currentPlan && subscriptionStore.currentPlanStatus === 'CANCEL') {
    openResubscriptionPopup(props.billingCycle);
    return;
  }

  if (!hasRegisteredCard()) {
    const registeredCardType = await openCardRegisterPopup();
    if (!registeredCardType) {
      trackEventFunnel('close_card_register_popup');
      return;
    }
  }

  const selectedCardType = await openCardManagePopup('PAYMENT', subscriptionStore.paymentType);
  if (!selectedCardType) {
    trackEventFunnel('close_card_select_popup');
    return;
  }

  openSubscriptionPaymentPopup(props.plan, props.billingCycle);
};

const goToContactPage = () => {
  trackEventFunnel('redirect_contact_page');

  router.push({ name: 'ss-contact' });
};

const openUnSubscriptionPopup = () => {
  trackEventFunnel('open_unsubscription_popup');

  setTypeSurveyUnsubscribe();
  openModalSurvey();
};

watch(
  () => props.planDetails,
  (newVal) => {
    if (!newVal) {
      return;
    }

    creditPerMonth.value = newVal.price ?? '';
    languageSupport.value = newVal.language ?? '';
    interpretationList.value = newVal.pricePerChat ?? [];
    additionalList.value = newVal.features ?? [];
  },
  { immediate: true, deep: true }
);
</script>

<style scoped lang="scss">
.plan-top-info {
  min-height: 120px;
  margin-bottom: 12px;
  .plan-tit {
    min-height: 80px;
    .flex-btw {
      margin-bottom: $size-xs;
      .inr {
        display: flex;
        align-items: center;
        gap: $size-s;
        .tit-h4 {
          min-height: 28px;
          line-height: 130%;
        }
      }
      .my-ck {
        width: 26px;
        aspect-ratio: 1/1;
        border-radius: 50%;
        background: $color-green-dark;
        display: flex;
        align-items: center;
        justify-content: center;
        svg {
          color: #fff;
          width: 1rem;
          height: 1rem;
        }
      }
    }

    .sub-txt {
      font-size: $font-size-psmall;
    }
  }
}
.card {
  button {
    &.xl-btn {
      &:disabled {
        background: $color-gray-60;
      }
    }
  }
}

.wrapper-price {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  gap: 0.25rem;

  @include respond-to('mobile-landscape') {
    height: fit-content;
  }
}

.plan-price {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
}

.discount {
  margin-top: 0.5rem;
}

.benefit-area {
  margin-top: 2rem;
  .benefit-list {
    > li {
      padding-bottom: 1.5rem;
      &:last-child {
        padding-bottom: 0;
      }
      .icon-tit {
        display: flex;
        align-items: center;
        gap: $size-xs;
        padding-bottom: $size-xs;
        img {
          width: 1.25rem;
          object-fit: contain;
        }
      }
      .category-tit {
        font-weight: 600;
      }
      .bf-item {
        li {
          padding: $size-xs 0;
          display: flex;
          align-items: center;
          gap: $size-xs;
          svg {
            color: $color-gray-600;
          }
        }
      }
    }
  }
}

.price-wrap {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  a {
    font-size: $font-size-psmall;
  }
}
</style>
